import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Link,
} from '@mui/material';
import UsbIcon from '@mui/icons-material/Usb';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FirmwareVersionPicker from '../FirmwareVersionPicker';
import ESPWebTools from '../ESPWebTools';
import { API_ENDPOINT } from '../../services/common';

const SENSOR_MODEL = 'PTQS1005';

export default function FirmwareUpgradeCard({ onBack, onConnect }) {
  const [manifestPath, setManifestPath] = useState(
    `${API_ENDPOINT}/firmware/manifest?model=${SENSOR_MODEL}`
  );
  const [flashComplete, setFlashComplete] = useState(false);

  const handleVersionSelect = useCallback((version) => {
    setManifestPath(
      `${API_ENDPOINT}/firmware/manifest?model=${SENSOR_MODEL}&version=${version}`
    );
  }, []);

  // Listen for ESP Web Tools success event
  useEffect(() => {
    const handleSuccess = () => setFlashComplete(true);
    window.addEventListener('esp-web-install-success', handleSuccess);
    return () => window.removeEventListener('esp-web-install-success', handleSuccess);
  }, []);

  if (flashComplete) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
          textAlign: 'center',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Firmware updated!
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 400 }}
        >
          Your sensor is ready. You may need to unplug and reconnect the USB
          cable.
        </Typography>
        <Button
          variant="contained"
          startIcon={<UsbIcon />}
          onClick={onConnect}
        >
          Connect to Sensor Manager
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
      }}
    >
      <Card sx={{ maxWidth: 520, width: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Update Sensor Firmware
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your sensor needs a one-time firmware update to work with Sensor
            Manager. Select a version and click the install button below.
          </Typography>

          <FirmwareVersionPicker
            model={SENSOR_MODEL}
            onVersionSelect={handleVersionSelect}
          />

          <ESPWebTools
            title="VPSI Air Quality Sensor"
            description="This will install the selected firmware on your sensor."
            manifestPath={manifestPath}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Before installing:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Close any applications using the serial port (Arduino IDE, Serial Monitor, etc.)</li>
              <li>Check that your USB cable is securely connected</li>
              <li>
                Windows users may need{' '}
                <Link
                  href="https://learn.sparkfun.com/tutorials/how-to-install-ch340-drivers#drivers-if-you-need-them"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CH340 drivers
                </Link>
              </li>
            </ul>
          </Alert>
        </CardContent>
      </Card>

      <Link
        component="button"
        variant="body2"
        onClick={onBack}
        sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <ArrowBackIcon fontSize="small" />
        Back
      </Link>
    </Box>
  );
}

FirmwareUpgradeCard.propTypes = {
  onBack: PropTypes.func.isRequired,
  onConnect: PropTypes.func.isRequired,
};
